/**
 * Auth state management — wired to Supabase Auth.
 *
 * Manages authentication state for the dashboard.
 * Uses Zustand with immer for immutable updates.
 *
 * Flow:
 * 1. LoginPage calls `signIn(email, password)`
 * 2. signIn hits `supabase.auth.signInWithPassword()`
 * 3. onAuthStateChange fires → syncs session to store + api-client
 * 4. Logout calls `supabase.auth.signOut()`
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { setAuthToken } from '@/lib/api/api-client';

export interface AuthState {
  isAuthenticated: boolean;
  email: string | null;
  token: string | null;
  doctorId: string | null;
  clinicId: string | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

interface AuthActions {
  /** Sign in via Supabase Auth. */
  signIn: (email: string, password: string) => Promise<void>;
  /** Sign out via Supabase Auth. */
  signOut: () => Promise<void>;
  /** Internal: update state from a Supabase session. */
  syncSession: (session: {
    access_token: string;
    user: { email?: string; user_metadata?: Record<string, unknown> };
  } | null) => void;
  /** Legacy login for testing / mock scenarios. */
  login: (email: string, token: string, doctorId: string, clinicId?: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInitialized: () => void;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  isAuthenticated: false,
  email: null,
  token: null,
  doctorId: null,
  clinicId: null,
  loading: false,
  error: null,
  initialized: false,
};

export const useAuthStore = create<AuthStore>()(
  immer((set, get) => ({
    ...initialState,

    signIn: async (email, password) => {
      set((state) => {
        state.loading = true;
        state.error = null;
      });

      try {
        // Lazy import to avoid circular deps and allow tests to mock
        const { getSupabaseClient } = await import('@/lib/supabase');
        const supabase = getSupabaseClient();

        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          set((state) => {
            state.error = error.message;
            state.loading = false;
          });
          return;
        }

        // Session sync happens via onAuthStateChange listener,
        // but we clear loading here in case the listener fires synchronously
        set((state) => {
          state.loading = false;
        });
      } catch (err) {
        set((state) => {
          state.error = err instanceof Error ? err.message : 'Sign-in failed';
          state.loading = false;
        });
      }
    },

    signOut: async () => {
      try {
        const { getSupabaseClient } = await import('@/lib/supabase');
        const supabase = getSupabaseClient();
        await supabase.auth.signOut();
      } catch {
        // Best-effort — clear local state regardless
      }

      set((state) => {
        state.isAuthenticated = false;
        state.email = null;
        state.token = null;
        state.doctorId = null;
        state.clinicId = null;
        state.loading = false;
        state.error = null;
      });

      setAuthToken(null);
    },

    syncSession: (session) => {
      if (session) {
        const meta = session.user.user_metadata ?? {};
        set((state) => {
          state.isAuthenticated = true;
          state.email = session.user.email ?? null;
          state.token = session.access_token;
          state.doctorId = (meta['doctor_id'] as string) ?? null;
          state.clinicId = (meta['clinic_id'] as string) ?? null;
          state.loading = false;
          state.error = null;
        });
        setAuthToken(session.access_token);
      } else {
        set((state) => {
          state.isAuthenticated = false;
          state.email = null;
          state.token = null;
          state.doctorId = null;
          state.clinicId = null;
          state.loading = false;
        });
        setAuthToken(null);
      }
    },

    // Legacy login — kept for tests and mock scenarios
    login: (email, token, doctorId, clinicId) => {
      set((state) => {
        state.isAuthenticated = true;
        state.email = email;
        state.token = token;
        state.doctorId = doctorId;
        state.clinicId = clinicId ?? null;
        state.loading = false;
        state.error = null;
      });
      setAuthToken(token);
    },

    logout: () => {
      set((state) => {
        state.isAuthenticated = false;
        state.email = null;
        state.token = null;
        state.doctorId = null;
        state.clinicId = null;
        state.loading = false;
        state.error = null;
      });
      setAuthToken(null);
    },

    setLoading: (loading) => {
      set((state) => {
        state.loading = loading;
      });
    },

    setError: (error) => {
      set((state) => {
        state.error = error;
        state.loading = false;
      });
    },

    setInitialized: () => {
      set((state) => {
        state.initialized = true;
      });
    },
  })),
);
